import { useMemo } from "react";
import MultiSelectDropdown from "../../../../../components/form/MultiSelectDropdown.tsx";
import Dropdown, { type Option } from "../../../../../components/general/Dropdown.tsx";
import { SecondaryButton } from "../../../../../components/general/SecondaryButton.tsx";
import { fieldLabelClasses } from "../../../../../components/general/fieldStyles.ts";
import {
    DateRangeInput,
    type DateRangeValue,
} from "../../../../../components/general/DateRangeInput";
import { cn } from "../../../../../lib/utils.ts";
import {
    InvoiceDateFieldEnum,
    type InvoiceDateFieldOption,
    type InvoiceFilterState,
} from "./types/invoiceFilter.ts";
import {
    createEmptyInvoiceFilterState,
    isInvoiceFilterActive,
} from "./utils/invoiceFilters.ts";

export interface InvoiceFilterBarProps {
    value: InvoiceFilterState;
    onChange: (filters: InvoiceFilterState) => void;
    relationOptions: string[];
    relationLabel: string;
    relationPlaceholder: string;
    dateLabel: string;
    dateFieldOptions?: InvoiceDateFieldOption[];
}

const DATE_FIELD_LABEL = "Datumtype";
const CLEAR_FILTERS_LABEL = "Wissen";

const NO_DATE_FIELD_OPTIONS: InvoiceDateFieldOption[] = [];
const FIRST_DATE_FIELD_OPTION = 0;

export function InvoiceFilterBar({
    value,
    onChange,
    relationOptions,
    relationLabel,
    relationPlaceholder,
    dateLabel,
    dateFieldOptions,
}: InvoiceFilterBarProps) {
    const dateFieldDropdownOptions = useMemo<Option[]>(
        () =>
            (dateFieldOptions ?? NO_DATE_FIELD_OPTIONS).map(({ value: field, label }) => ({
                value: field,
                label,
            })),
        [dateFieldOptions]
    );

    const selectedDateFieldOption =
        dateFieldDropdownOptions.find((option) => option.value === value.dateField) ??
        dateFieldDropdownOptions[FIRST_DATE_FIELD_OPTION];

    return (
        <div className="mb-4 flex w-full flex-wrap items-end justify-between gap-4">
            <div className="flex flex-wrap items-end gap-4">
                {selectedDateFieldOption && (
                    <div className="w-48">
                        <label className={cn(fieldLabelClasses, "mb-2")}>{DATE_FIELD_LABEL}</label>
                        <Dropdown
                            selected={selectedDateFieldOption}
                            options={dateFieldDropdownOptions}
                            onUpdateFunc={(dateField) =>
                                onChange({ ...value, dateField: dateField as InvoiceDateFieldEnum })
                            }
                        />
                    </div>
                )}

                <div>
                    <label className={cn(fieldLabelClasses, "mb-2")}>{dateLabel}</label>
                    <div className="w-[234px]">
                        <DateRangeInput
                            value={value.dateRange}
                            onChange={(dateRange: DateRangeValue) =>
                                onChange({ ...value, dateRange })
                            }
                        />
                    </div>
                </div>

                <div>
                    <label className={cn(fieldLabelClasses, "mb-2")}>{relationLabel}</label>
                    <MultiSelectDropdown
                        className="w-56"
                        options={relationOptions}
                        selectedValues={value.relations}
                        onSelectionChange={(relations) => onChange({ ...value, relations })}
                        placeholder={relationPlaceholder}
                        displayValue={(relation) => relation}
                    />
                </div>
            </div>

            <SecondaryButton
                title={CLEAR_FILTERS_LABEL}
                disabled={!isInvoiceFilterActive(value)}
                onClick={() => onChange(createEmptyInvoiceFilterState(value.dateField))}
            />
        </div>
    );
}
